import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

interface LoadingAnimationProps {
  message?: string;
  subMessage?: string;
  durationMs?: number; // total duration for progress animation
  completeNow?: boolean; // when true, complete to 100% quickly
  onCompleted?: () => void; // called once when progress reaches 100%
  showCountdown?: boolean; // show countdown timer with approximate time remaining
  exiting?: boolean; // when true, trigger exit animation
  onExitComplete?: () => void; // called when exit animation finishes
  countdownDoneText?: string; // text shown when countdown reaches 0
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ message = 'Generando imágenes...', subMessage, durationMs = 30000, completeNow = false, onCompleted, showCountdown = false, exiting = false, onExitComplete, countdownDoneText }) => {
  if (typeof document === 'undefined') return null;

  // Animation states for enter/exit
  const [isEntering, setIsEntering] = useState<boolean>(true);
  const [isExiting, setIsExiting] = useState<boolean>(false);

  // Progress bar logic (programmatic, supports forced completion)
  const [progress, setProgress] = useState<number>(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(Math.ceil(durationMs / 1000));
  const completingRef = useRef<boolean>(false);
  const startTimeRef = useRef<number | null>(null);
  const completeStartRef = useRef<number | null>(null);
  const startProgressRef = useRef<number>(0);
  const doneRef = useRef<boolean>(false);
  const rafRef = useRef<number | null>(null);

  // Trigger enter animation on mount
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setIsEntering(false);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  // Handle exit animation when exiting prop changes
  useEffect(() => {
    if (exiting && !isExiting) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        onExitComplete?.();
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [exiting, isExiting, onExitComplete]);

  // Countdown timer for video generation
  useEffect(() => {
    if (!showCountdown) return;
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showCountdown]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Main RAF loop
  useEffect(() => {
    const step = (now: number) => {
      if (doneRef.current) return;
      if (startTimeRef.current === null) startTimeRef.current = now;

      if (completingRef.current) {
        if (completeStartRef.current === null) {
          completeStartRef.current = now;
          startProgressRef.current = progress;
        }
        const completeDuration = 400; // ms for forced completion
        const t = Math.min(1, (now - completeStartRef.current) / completeDuration);
        const eased = 1 - Math.pow(1 - t, 3);
        const next = startProgressRef.current + (100 - startProgressRef.current) * eased;
        const clamped = next >= 99.9 ? 100 : next;
        setProgress(clamped);
        if (clamped >= 100) {
          doneRef.current = true;
          onCompleted && onCompleted();
          return;
        }
      } else {
        const elapsed = now - startTimeRef.current;
        const t = Math.min(1, elapsed / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        const pct = eased * 100;
        const cap = 97; // do not auto-complete; wait for completeNow
        const clamped = pct >= cap ? cap : pct;
        setProgress(clamped);
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [durationMs, onCompleted, progress]);

  // Trigger forced completion when prop flips to true
  useEffect(() => {
    if (completeNow && !doneRef.current) {
      completingRef.current = true;
      // reset completion timing
      completeStartRef.current = null;
    }
  }, [completeNow]);

  return createPortal(
    <div 
      className={`fixed inset-0 flex items-center justify-center z-[100000] transition-all duration-300 ease-out
        md:bg-brand-black/50 md:backdrop-blur-sm md:px-4
        ${isEntering ? 'md:opacity-0 translate-x-full md:translate-x-0' : isExiting ? 'opacity-0 translate-x-0 pointer-events-none' : 'opacity-100 translate-x-0'}`}
    >
      {/* Local keyframes to ensure smooth, progressive animation independent of React renders */}
      <style>{`@keyframes atlasProgress { from { width: 0% } to { width: 100% } }`}</style>
      <div className="bg-surface-card shadow-lg w-full relative overflow-hidden h-full md:rounded-lg md:max-w-sm lg:max-w-lg xl:max-w-xl md:h-auto">
        <div className="p-6 sm:p-8 md:pt-8 md:pb-8 md:px-10 w-full flex flex-col justify-center items-center h-full">
          <div className="w-64 h-64 md:w-56 md:h-56 lg:w-64 lg:h-64">
          <DotLottieReact
            src="https://lottie.host/848266a7-3f51-41c9-bbc0-b55500402910/XzeuQRXrLQ.lottie"
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
          </div>
          <div className="w-full mt-4 text-center">
          <h3 className="text-[17px] font-semibold text-brand-black">{message}</h3>
          {subMessage && <p className="text-[15px] text-neutral-500 mt-1">{subMessage}</p>}
          {showCountdown && remainingSeconds > 0 && (
            <p className="text-[13px] text-neutral-500 mt-2">
              Tiempo aprox. restante: <span className="font-medium text-neutral-700">{formatTime(remainingSeconds)}</span>
            </p>
          )}
          {showCountdown && remainingSeconds === 0 && (
            <p className="text-[13px] text-neutral-500 mt-2">
              {countdownDoneText || 'Tu video estará disponible en breve'}
            </p>
          )}
          {/* Progress bar below text on mobile */}
          <div className="mt-6 w-full h-1.5 bg-neutral-200 rounded-full md:hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${Math.round(progress)}%`, background: 'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)' }}
            />
          </div>
          </div>
        </div>
        {/* Progress bar flush to the bottom edge of the card - desktop only */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-neutral-200 hidden md:block">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${Math.round(progress)}%`, background: 'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)' }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-label="Progreso de generación"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LoadingAnimation;
